import React, { useCallback } from 'react';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { startForgotEmail } from '../server';
import * as Types from '../types';

export const useStartForgotEmail = ({ setToken }: { setToken: React.Dispatch<React.SetStateAction<string>> }) => {
    const isMounted = useIsMounted();
    const casinoName = useAppSelector(prop('casinoName'));
    const getErrorMessage = useGetErrorMessage();
    const translate = useComponentTranslations();

    return useCallback(async function process({ data, validationToken, requestReCaptchaToken, abortReCaptchaTokenRequest }: {
        data: Types.IStartForgotEmailFormData;
        validationToken?: string;
        requestReCaptchaToken: () => Promise<{ token: string }>;
        abortReCaptchaTokenRequest: () => void;
    }): Promise<string | void> {
        const {
            cellphone,
            lastname,
            birthdate,
            personalId
        } = data;

        try {
            const response = await startForgotEmail({
                casinoName,
                personalId,
                cellPhone: cellphone,
                lastName: lastname,
                birthDate: birthdate,
                token: validationToken
            });
            const { data: { token } = {} } = response;
            abortReCaptchaTokenRequest();

            if (!token) {
                return translate('error');
            }

            isMounted() && setToken(token);
        } catch (error: Types.TStartForgotEmailError) {
            const { data: { errorCode = 0 } = {} } = error?.response || error || {};
            const errorMsg = getErrorMessage(error);

            if (errorCode === 301) {
                try {
                    const { token: externalValidationToken } = await requestReCaptchaToken();

                    return await process({
                        data,
                        requestReCaptchaToken,
                        abortReCaptchaTokenRequest,
                        validationToken: externalValidationToken
                    });
                } catch {
                    return translate('error');
                }
            }

            return errorMsg;
        }
    }, [setToken, casinoName, getErrorMessage, translate, isMounted]);
};
